import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import { clearImportAccountMap, createImportAccount, saveImportAccountMap } from '../../../redux/actions/import.js';
import Button from '../../components/Button.jsx';
import PlaidAccountSelect, {
	ACCOUNT_SELECT_CREATE,
	ACCOUNT_SELECT_NONE,
	ACCOUNT_SELECT_SKIP,
} from '../../components/PlaidAccountSelect.jsx';

/**
 * One row per account label the export used. Nothing is decided automatically -- a suggestion is
 * pre-selected in the dropdown where the match is convincing, but it only takes effect once it is
 * saved, so an account is never silently mapped to the wrong card.
 */
export default function ImportAccountMapper() {
	const dispatch = useDispatch();
	const batch = useSelector((state) => state.importing.selectedBatch);
	const accounts = useSelector((state) => state.importing.accounts);
	const availableAccounts = useSelector((state) => state.importing.availableAccounts);
	const accountTypes = useSelector((state) => state.importing.accountTypes);

	if(!batch || !accounts.length) {
		return null;
	}

	const mappedCount = accounts.filter((account) => !!account.map).length;
	/** A balance file has no direction of travel to show, so that column carries its two ends instead. */
	const isBalances = batch.kind === 'balances';

	return (
		<div className='import-account-mapper'>
			<h6>
				Accounts{' '}
				<span className='grey-text'>
					({mappedCount} of {accounts.length} mapped)
				</span>
			</h6>
			<p className='grey-text'>
				Each account in the file needs to point at a bank account in this budget. Mappings are
				saved against the budget, so the next export you upload will already know them &mdash; a
				balance export and a transaction export from the same service share them outright. An
				account the file has and this budget does not &mdash; a closed card, cash, an account that
				was never linked &mdash; can be created here rather than skipped.
			</p>

			<div className='import-table-scroll'>
				<table className='import-table import-map-table'>
					<thead>
						<tr>
							<th>In the file</th>
							<th className='right-align'>Rows</th>
							<th>Dates</th>
							<th className='right-align'>{isBalances ? 'First / last' : 'Out / In'}</th>
							<th>Maps to</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{accounts.map((account) => (
							<AccountMapRow
								key={account.external_account}
								account={account}
								availableAccounts={availableAccounts}
								accountTypes={accountTypes}
								onSave={(plaidAccountId, action) =>
									dispatch(saveImportAccountMap(batch.id, account.external_account, plaidAccountId, action))}
								onClear={() => dispatch(clearImportAccountMap(batch.id, account.external_account))}
								onCreate={(newAccount) =>
									dispatch(createImportAccount(batch.id, account.external_account, newAccount))}
							>
								<td className='right-align import-map-detail'>
									{account.row_count}
									<span className='import-mobile-only'> {account.row_count === 1 ? 'row' : 'rows'}</span>
								</td>
								<td className='grey-text import-map-detail'>{dateRange(account.first_date, account.last_date)}</td>
								<td className='right-align grey-text import-map-detail'>
									{/** Two whole figures that may sit one over the other, so the column need not fit both. */}
									{isBalances
										? <><span className='import-nowrap'>{moneyFormatFromCents(account.first_balance)} /</span> <span className='import-nowrap'>{moneyFormatFromCents(account.last_balance)}</span></>
										: <><span className='import-nowrap'>{moneyFormatFromCents(-account.outflow)} /</span> <span className='import-nowrap'>{moneyFormatFromCents(account.inflow)}</span></>
									}
								</td>
							</AccountMapRow>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

/**
 * A file's span of dates for one account: one date when it starts and ends on the same day, otherwise
 * both, each kept whole so the range breaks between them and nowhere else.
 */
export function dateRange(first, last) {
	if(!first || first === last) {
		return <span className='import-nowrap'>{first || last || ''}</span>;
	}
	return <><span className='import-nowrap'>{first} &ndash;</span> <span className='import-nowrap'>{last}</span></>;
}

/**
 * One label and the account it maps to: the picker, starting on the stored map or the best
 * suggestion, the form for creating an account in its place, and Save or Clear. `account` carries
 * `external_account`, `map`, `suggestions` and `new_account`, as Import_Domain answers them for a batch
 * (accounts()) and for Monarch's own list (refresh_monarch_balances()). The cells between the label and
 * the picker are the caller's, as `children`.
 *
 * `takenAccountIds`, a Set of account ids other labels already map to, keeps a suggestion of one of
 * those from being preselected; it can still be picked by hand. `notice` is said under the picker, and
 * `error` is why this row's last save was refused.
 */
export function AccountMapRow({
	account, availableAccounts, accountTypes, onSave, onClear, onCreate, children, takenAccountIds, notice, error,
}) {
	const map = account.map;
	const topSuggestion = account.suggestions && account.suggestions.length ? account.suggestions[0] : null;
	const suggestionTaken = !map && !!topSuggestion && !!takenAccountIds
		&& takenAccountIds.has(Number(topSuggestion.plaid_account_id));
	const suggestion = suggestionTaken ? null : topSuggestion;

	/** An unmapped row starts on its suggestion when there is one, so the common case is one click. */
	const initialValue = map
		? (map.action === 'skip' ? ACCOUNT_SELECT_SKIP : String(map.plaid_account_id))
		: (suggestion ? String(suggestion.plaid_account_id) : ACCOUNT_SELECT_NONE);
	const [value, setValue] = React.useState(initialValue);

	/** A save re-fetches the list, so re-sync when this row's stored mapping changes underneath us. */
	React.useEffect(() => { setValue(initialValue); }, [initialValue]);

	const isCreating = value === ACCOUNT_SELECT_CREATE;
	const isDirty = value !== initialValue && !isCreating;
	const isUnsavedSuggestion = !map && !!suggestion && value === String(suggestion.plaid_account_id);

	const save = () => {
		if(value === ACCOUNT_SELECT_SKIP) {
			onSave(null, 'skip');
			return;
		}
		if(value === ACCOUNT_SELECT_NONE) {
			return;
		}
		onSave(Number(value), 'assign');
	};

	let statusClass = 'import-status-unmapped';
	if(map) {
		statusClass = map.action === 'skip' ? 'import-status-skipped' : 'import-status-mapped';
	}

	return (
		<tr className={`import-map-row ${statusClass}`}>
			<td>
				<span className='import-external-key'>{account.external_account}</span>
				{!map && <span className='import-chip-unmapped'>Not mapped</span>}
			</td>
			{children}
			<td className='import-map-picker'>
				<PlaidAccountSelect
					accounts={availableAccounts}
					value={value}
					onChange={setValue}
					allowCreate={!map}
				/>
				{isCreating &&
				<NewAccountForm
					defaults={account.new_account}
					accountTypes={accountTypes}
					onCancel={() => setValue(initialValue)}
					onCreate={onCreate}
				/>
				}
				{isUnsavedSuggestion &&
				<div className='import-suggestion grey-text'>
					Suggested: {suggestion.reason} ({suggestion.confidence}% confident) &mdash; not saved yet
				</div>
				}
				{suggestionTaken &&
				<div className='import-suggestion grey-text'>
					The closest match ({topSuggestion.reason}) is already mapped from another account
				</div>
				}
				{!map && !topSuggestion &&
				<div className='import-suggestion grey-text'>
					No match in this budget &mdash; this looks like a new account
				</div>
				}
				{!!notice && <div className='import-suggestion grey-text import-row-notice'>{notice}</div>}
				{!!error && <div className='import-suggestion red-text import-row-error'>{error}</div>}
			</td>
			<td className='right-align import-map-actions'>
				{(isDirty || isUnsavedSuggestion) && !isCreating &&
				<Button small onClick={save} disabled={value === ACCOUNT_SELECT_NONE}>Save</Button>
				}
				{!!map && !isDirty && !isCreating &&
				<Button small flat onClick={onClear}>Clear</Button>
				}
			</td>
		</tr>
	);
}

/**
 * The details a new account needs that the file cannot supply. Name and mask are prefilled from the
 * label -- the server derives them, so they read it the same way the matcher does -- but the type is
 * asked outright: nothing in "Freedom" says whether it is a card or a checking account, and the type
 * is what later tells a balance which way to read.
 */
function NewAccountForm({ defaults, accountTypes, onCancel, onCreate }) {
	const suggested = defaults || {};
	const [name, setName] = React.useState(suggested.name || '');
	const [mask, setMask] = React.useState(suggested.mask || '');
	/** The type the label suggests where the server could tell -- Monarch names its own -- else the first. */
	const [type, setType] = React.useState(
		(suggested.type && (accountTypes || []).includes(suggested.type) && suggested.type)
		|| (accountTypes && accountTypes[0])
		|| 'depository'
	);

	return (
		<div className='import-create-account'>
			<div className='import-create-account-fields'>
				<label>
					<span className='grey-text'>Name</span>
					<input
						type='text'
						value={name}
						maxLength={64}
						onChange={(event) => setName(event.target.value)}
					/>
				</label>
				<label className='import-create-account-mask'>
					<span className='grey-text'>Last digits</span>
					<input
						type='text'
						value={mask}
						maxLength={20}
						placeholder='optional'
						onChange={(event) => setMask(event.target.value)}
					/>
				</label>
				<label>
					<span className='grey-text'>Type</span>
					<select
						className='browser-default'
						value={type}
						onChange={(event) => setType(event.target.value)}
					>
						{(accountTypes || []).map((option) => (
							<option key={option} value={option}>{option}</option>
						))}
					</select>
				</label>
			</div>
			<div className='import-create-account-actions'>
				<Button
					small
					disabled={!name.trim()}
					onClick={() => onCreate({ name: name.trim(), mask: mask.trim(), type })}
				>
					Create and map
				</Button>
				<Button small flat onClick={onCancel}>Cancel</Button>
			</div>
		</div>
	);
}
